import { useToasts } from "@certn/cascadia";
import { useMediaQuery } from "@certn/cascadia-react/hooks/useMediaQuery";
import { breakpoints } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { useContext, useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";

import { SectionsListItem } from "./SectionsListItem";
import { useCheckFlowNavbarStyles } from "./styles";
import { ApplicationContext } from "../Application.context";
import { SectionKey } from "../Section";
import { CheckEngineAPIServerError } from "../errors";
import { getField } from "../fields";
import { useSectionNavigation } from "../hooks";

type SectionsListProps = {
  onOverflow: (overflow: boolean) => void;
  onScrollToBottom: (isScrolledToBottom: boolean) => void;
  onClose: () => void;
};

export function SectionsList({
  onOverflow,
  onScrollToBottom,
  onClose,
}: SectionsListProps) {
  const { t } = useTranslation();
  const styles = useCheckFlowNavbarStyles();
  const { navigateToSection, navigateToSummary } = useSectionNavigation();
  const listRef = useRef(null);

  const { sections, saveData, currentSectionKey } =
    useContext(ApplicationContext);

  const isMobile = useMediaQuery(
    `(max-width: ${breakpoints["breakpoint-sm"].maxWidth.toString()}px)`
  );
  const { show } = useToasts();

  const calculateOverflow = () => {
    if (listRef.current) {
      const element: HTMLElement = listRef.current;

      onScrollToBottom(
        element.scrollHeight <= element.clientHeight + element.scrollTop
      );

      onOverflow(element.scrollHeight > element.clientHeight);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", calculateOverflow);

    return () => {
      window.removeEventListener("resize", calculateOverflow);
    };
  }, []);

  const handleNavigate = async (key: string) => {
    try {
      await saveData({ sectionKey: currentSectionKey });
    } catch (e) {
      if (e instanceof CheckEngineAPIServerError) {
        show({ title: t("Something went wrong"), type: "error" });
        // If there is an API error, do not continue
        return;
      }
    }

    await navigateToSection(key);
  };

  const handleNavigateToSummary = async () => {
    try {
      await saveData({ sectionKey: currentSectionKey });
    } catch (e) {
      if (e instanceof CheckEngineAPIServerError) {
        show({ title: t("Something went wrong"), type: "error" });
        // If there is an API error, do not continue
        return;
      }
    }

    await navigateToSummary();
  };

  return (
    <div
      ref={listRef}
      className={css(styles.listContainer)}
      onScroll={calculateOverflow}
    >
      <ul className={styles.list}>
        {sections?.map((section) => {
          // isCompleted prop depends on ApplicationContext (i.e. localStorage) rather than the api.
          // isCompleted variable in this method is derived from value field on Field model from api.
          // isCompleted variable only implemented for Consents so far, hence '|| isCompleted === undefined'
          const isAllFieldsCompleted = section.fields
            .map(getField)
            .filter(Boolean)
            .every(
              ({ isCompleted }) => isCompleted || isCompleted === undefined
            );

          return (
            <SectionsListItem
              key={section.key}
              sectionKey={section.key}
              onPress={async () => {
                handleNavigate(section.key);
                isMobile && onClose();
              }}
              isActive={currentSectionKey === section.key}
              icon="pending_actions"
              isDisabled={section.isDisabled}
              isCompleted={
                !section.isDisabled &&
                section.isCompleted &&
                isAllFieldsCompleted
              }
              hasUnsavedValues={Boolean(section.unsavedValues)}
              testID="application-navigation-panel-button"
            >
              {section.title}
            </SectionsListItem>
          );
        })}

        <SectionsListItem
          isActive={currentSectionKey === SectionKey.SUMMARY}
          sectionKey="summary"
          onPress={async () => {
            handleNavigateToSummary();
            isMobile && onClose();
          }}
          icon="overview"
          isDisabled={sections.some((section) => section.isDisabled)}
          testID="application-navigation-panel-button"
        >
          <Trans>Review & Submit</Trans>
        </SectionsListItem>
      </ul>
    </div>
  );
}
